// ────────────────────────────────────────────────────────────────────────────
// Token interpolation for free-text bands (text block, footer, header title).
//
// Structured bands cover the fixed anatomy of an invoice; tokens cover the
// rest — "Payment due within {{doc.paymentTerms}} to {{bank.iban}}, quoting
// {{doc.number}}". A token is `{{group.key}}` and resolves against a flat map
// built from `DocumentData`, with money and dates already formatted for the
// document currency.
//
// Unknown tokens render as their literal `{{…}}` text rather than an empty
// string: a typo that silently prints nothing is far harder to spot in a
// preview than one that prints itself.
// ────────────────────────────────────────────────────────────────────────────
import type { DocumentData } from './documentData';

export function formatMoney(value: number, isoCode: string | null | undefined): string {
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: isoCode || 'EUR',
    }).format(value);
  } catch {
    // Unknown ISO code — fall back to a bare 2dp figure rather than throwing
    // mid-render and blanking the whole document.
    return value.toFixed(2);
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
}

export function formatQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-IE', { maximumFractionDigits: 3 }).format(value);
}

/** A token offered in the builder, grouped for the picker. */
export interface TokenDescriptor {
  token: string;
  label: string;
  group: string;
}

export const TOKEN_CATALOGUE: TokenDescriptor[] = [
  { token: 'doc.typeName', label: 'Document type (Invoice / Credit note)', group: 'Document' },
  { token: 'doc.number', label: 'Document number', group: 'Document' },
  { token: 'doc.issueDate', label: 'Issue date', group: 'Document' },
  { token: 'doc.dueDate', label: 'Due date', group: 'Document' },
  { token: 'doc.poNumber', label: 'PO number', group: 'Document' },
  { token: 'doc.reference', label: 'Reference', group: 'Document' },
  { token: 'doc.currency', label: 'Currency code', group: 'Document' },
  { token: 'doc.paymentTerms', label: 'Payment terms', group: 'Document' },
  { token: 'doc.message', label: 'Invoice message', group: 'Document' },
  { token: 'organisation.name', label: 'Organisation name', group: 'Organisation' },
  { token: 'organisation.address', label: 'Organisation address', group: 'Organisation' },
  { token: 'organisation.vatNumber', label: 'VAT number', group: 'Organisation' },
  { token: 'organisation.regNumber', label: 'Company reg. number', group: 'Organisation' },
  { token: 'organisation.phone', label: 'Phone', group: 'Organisation' },
  { token: 'organisation.email', label: 'Email', group: 'Organisation' },
  { token: 'organisation.website', label: 'Website', group: 'Organisation' },
  { token: 'customer.name', label: 'Customer name', group: 'Customer' },
  { token: 'customer.code', label: 'Customer code', group: 'Customer' },
  { token: 'customer.address', label: 'Billing address', group: 'Customer' },
  { token: 'customer.vatNumber', label: 'Customer VAT number', group: 'Customer' },
  { token: 'totals.subtotal', label: 'Subtotal ex VAT', group: 'Totals' },
  { token: 'totals.vat', label: 'VAT total', group: 'Totals' },
  { token: 'totals.total', label: 'Document total', group: 'Totals' },
  { token: 'bank.accountName', label: 'Account name', group: 'Bank' },
  { token: 'bank.bankName', label: 'Bank name', group: 'Bank' },
  { token: 'bank.iban', label: 'IBAN', group: 'Bank' },
  { token: 'bank.bic', label: 'BIC / SWIFT', group: 'Bank' },
  { token: 'bank.sortCode', label: 'Sort code', group: 'Bank' },
  { token: 'bank.accountNumber', label: 'Account number', group: 'Bank' },
];

/** Empty / missing values print an em dash so gaps read as intentional. */
function text(value: string | null | undefined): string {
  return value && value.trim() ? value : '—';
}

/** Flatten `DocumentData` into the `{{token}}` → printable-string map. */
export function buildTokenMap(data: DocumentData): Record<string, string> {
  const iso = data.doc.currencyIsoCode;
  return {
    'doc.typeName': data.doc.typeName,
    'doc.number': data.doc.number,
    'doc.issueDate': formatDate(data.doc.issueDate),
    'doc.dueDate': formatDate(data.doc.dueDate),
    'doc.poNumber': text(data.doc.poNumber),
    'doc.reference': text(data.doc.reference),
    'doc.currency': iso,
    'doc.paymentTerms': text(data.doc.paymentTerms),
    'doc.message': text(data.message),
    'organisation.name': data.organisation.name,
    'organisation.address': text(data.organisation.addressLines.join(', ')),
    'organisation.vatNumber': text(data.organisation.taxNumber),
    'organisation.regNumber': text(data.organisation.registrationNumber),
    'organisation.phone': text(data.organisation.phone),
    'organisation.email': text(data.organisation.email),
    'organisation.website': text(data.organisation.website),
    'customer.name': data.customer.name,
    'customer.code': text(data.customer.code),
    'customer.address': text(data.customer.address),
    'customer.vatNumber': text(data.customer.taxNumber),
    'totals.subtotal': formatMoney(data.totals.subtotal, iso),
    'totals.vat': formatMoney(data.totals.vatTotal, iso),
    'totals.total': formatMoney(data.totals.total, iso),
    'bank.accountName': text(data.bank?.accountName),
    'bank.bankName': text(data.bank?.bankName),
    'bank.iban': text(data.bank?.iban),
    'bank.bic': text(data.bank?.bic),
    'bank.sortCode': text(data.bank?.sortCode),
    'bank.accountNumber': text(data.bank?.accountNumber),
  };
}

const TOKEN_PATTERN = /\{\{\s*([\w.]+)\s*\}\}/g;

/** Replace every `{{token}}` in `template` from `map`; unknown keys stay put. */
export function resolveTokens(template: string, map: Record<string, string>): string {
  return template.replace(TOKEN_PATTERN, (whole, key: string) => map[key] ?? whole);
}
